"use client";

import { useRef, useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
} from "@/components/ui/sheet";
import { t } from "i18next";
import AudioRecorderUI from "../audio-recorder";
import RecorderForm, {
  type RecorderFormHandle,
} from "../audio-recorder/recorder-form";
import { useVisitsStore } from "@/store/visits.store";
import { useToast } from "@/hooks/use-toast";
import { useAuthStore } from "@/store/auth.store";
import getDuration from "@/lib/getDuration";
import { getBlobFromUrl } from "@/lib/getBlobFromUrl";
import RecorderAudioPlayer from "../audio-recorder/recorder-audio-player";
import ConfirmDialog from "../ConfirmDialog";
import type { RecorderFormSchemaType } from "@/schemas/newVisit.schema";
import { Button } from "@/components/ui/button";
import MicrophoneIcon from "@/assets/icons/mic.svg?react";
import { useRecordingUsageStore } from "@/store/recordingUsage.store";
import { SubscriptionOnlyButton } from "../SubscriptionOnlyButton/SubscriptionOnlyButton";
import { ValidationSuscriptionButton } from "@/common/enums/enums";
import { useBackgroundVisitUploadStore } from "@/store/backgroundVisitUpload.store";
import { useAudioValidation } from "@/hooks/useAudioValidation";
import { AlertMessage } from "../AlertMessage";
import { UploadAudioButton } from "./UploadRecordButton";

export function NewVisitSidebar({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const { toast } = useToast();
  const [showRecorder, setShowRecorder] = useState(false);
  const [isFileUpload, setIsFileUpload] = useState(false);
  const [audioFinished, setAudioFinished] = useState(false);
  const [recordedBlobUrl, setRecordedBlobUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const recorderFormRef = useRef<RecorderFormHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { loadingUploadFiles, errorUploadFiles, getFiles } = useVisitsStore();
  const { userData } = useAuthStore();
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const { fetchAndSetAvailableMinutes } = useRecordingUsageStore.getState();
  const { error, validateFile, resetError } = useAudioValidation();
  const alreadyCompletedRef = useRef<boolean>(false);
  const { enqueueVisitToUpload } = useBackgroundVisitUploadStore.getState();

  const handleRecordingComplete = (blobUrl: string): void => {
    if (alreadyCompletedRef.current) return;
    alreadyCompletedRef.current = true;

    setRecordedBlobUrl(blobUrl);
    setAudioFinished(true);
    if (userData) void fetchAndSetAvailableMinutes(userData.id);
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const file = event.target.files?.[0];
    if (file && validateFile(file)) {
      const fileUrl = URL.createObjectURL(file);
      setRecordedBlobUrl(fileUrl);
      setAudioFinished(true);
      setIsFileUpload(true);
      if (userData) void fetchAndSetAvailableMinutes(userData.id);
    }
  };

  const handleFormSubmit = async (
    formData: RecorderFormSchemaType
  ): Promise<void> => {
    if (!recordedBlobUrl) return;
    try {
      const audioBlob = await getBlobFromUrl(recordedBlobUrl);
      const duration = await getDuration(audioBlob);
      if (userData?.id) {
        await enqueueVisitToUpload({
          file: audioBlob,
          userId: userData.id,
          duration,
          style: formData.style,
          format: formData.format,
          exam: formData.examination,
          template: formData.templateName,
          macros: "",
          customTemplateId: formData.customTemplateId,
        });
        setSubmitting(true);
      } else {
        toast({
          variant: "error",
          title: t("visits.sidebar.messages.requestFailed.title"),
          description: t("visits.sidebar.messages.requestFailed.description"),
        });
      }
    } catch {
      toast({
        variant: "error",
        title: t("visits.sidebar.messages.requestFailed.title"),
        description: t("visits.sidebar.messages.requestFailed.description"),
      });
    }
  };

  useEffect(() => {
    if (submitting && !loadingUploadFiles) {
      if (!errorUploadFiles) {
        toast({ description: t("visits.sidebar.messages.fileEnqueued") });
        onOpenChange(false);
      } else {
        toast({
          variant: "error",
          title: t("visits.sidebar.messages.requestFailed.title"),
          description: t("visits.sidebar.messages.requestFailed.description"),
        });
      }
      setSubmitting(false);
    }
  }, [
    loadingUploadFiles,
    submitting,
    errorUploadFiles,
    onOpenChange,
    toast,
    getFiles,
    userData?.id,
  ]);

  const resetStates = (): void => {
    setShowRecorder(false);
    setIsFileUpload(false);
    setAudioFinished(false);
    setRecordedBlobUrl(null);
    setSubmitting(false);
    alreadyCompletedRef.current = false;
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    if (!open) {
      resetStates();
      resetError();
    }
  }, [open, resetError]);

  return (
    <Sheet
      open={open}
      onOpenChange={(isOpen) => {
        if (!loadingUploadFiles) {
          onOpenChange(isOpen);
        }
      }}
    >
      <SheetContent
        className="w-full sm:max-w-md p-0 sm:rounded-l-2xl"
        onEscapeKeyDown={(event) => {
          event.preventDefault();
        }}
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
      >
        <SheetTitle></SheetTitle>
        <SheetHeader />
        <input
          ref={fileInputRef}
          accept="audio/*"
          style={{ display: "none" }}
          type="file"
          onChange={handleFileChange}
        />
        <div className="flex flex-col flex-1 h-full">
          <div className="flex-1 flex flex-col items-center justify-center gap-8 py-6 px-10">
            {isFileUpload ? (
              recordedBlobUrl && (
                <RecorderAudioPlayer mediaBlobUrl={recordedBlobUrl} />
              )
            ) : showRecorder ? (
              <AudioRecorderUI
                onFinished={setAudioFinished}
                onRecordingComplete={handleRecordingComplete}
              />
            ) : (
              <>
                <MicrophoneIcon
                  aria-label="Mic"
                  height={100}
                  strokeWidth="0.5"
                  width={100}
                />
                <Button
                  className="w-full"
                  size={"xl"}
                  onClick={() => {
                    setShowRecorder(true);
                  }}
                >
                  {t("visits.sidebar.recordAudio")}
                </Button>
                <UploadAudioButton
                  onClick={() => fileInputRef.current?.click()}
                />
                {error && (
                  <AlertMessage
                    description={error}
                    title={t("error.formRecorder.titleMessage")}
                    variant="destructive"
                  />
                )}
              </>
            )}
            {audioFinished && (
              <RecorderForm
                ref={recorderFormRef}
                onSubmitData={handleFormSubmit}
              />
            )}
          </div>
          <SheetFooter className="bg-tinted-grey-1 rounded-bl-2xl flex flex-col sm:space-x-0 sm:flex-col sm:justify-center content-center px-10 py-6 gap-2">
            {audioFinished && (
              <SubscriptionOnlyButton
                className="w-full"
                disabled={loadingUploadFiles}
                size={"xl"}
                validations={[ValidationSuscriptionButton.RECORDING_USAGES]}
                onClick={async () => {
                  await recorderFormRef.current?.submitForm();
                }}
              >
                {loadingUploadFiles
                  ? `${t("visits.sidebar.procesing")}...`
                  : t("visits.sidebar.submit")}
              </SubscriptionOnlyButton>
            )}
            <ConfirmDialog
              description={t("visits.sidebar.cancelConfirmation.description")}
              open={showCancelConfirmation}
              title={t("visits.sidebar.cancelConfirmation.title")}
              trigger={
                <Button
                  className="w-full"
                  disabled={loadingUploadFiles}
                  size={"xl"}
                  variant={"grey"}
                >
                  {t("visits.sidebar.cancel")}
                </Button>
              }
              onOpenChange={setShowCancelConfirmation}
              onConfirm={() => {
                if (!loadingUploadFiles) onOpenChange(false);
              }}
            />
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  );
}
